import React, { type ReactNode } from 'react';
import { styled as styled2, type StyledProps } from '@compiled/react';
import { Flex, xcss } from '@atlaskit/primitives';

import { token } from '@atlaskit/tokens';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles
export const ObjectDetailsSpacer: React.ComponentType<
	React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> & StyledProps
> = // eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
	styled2.div({
		paddingTop: token('space.100'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.075'),
	});

const readViewContainerStyles = xcss({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '20px',
	minHeight: '18px',
	whiteSpace: 'pre-wrap',
	wordWrap: 'break-word',
	flexFlow: 'wrap',
});

const readViewContainerPlaceholderStyles = xcss({
	color: 'color.text.subtlest',
	wordBreak: 'break-word',
	overflowWrap: 'break-word',
});

const readViewContainerNonPlaceholderStyles = xcss({
	wordBreak: 'break-all',
});

/**
 * Note: this container only lays out children but does not provide its own margins / padding - that is for consumers to
 * do, to allow this to be more re-usable. E.g. if this provides 8px padding and consumers want to have 24px, then it
 * gets very fragile having to add 16px (8 + 16 = 24) padding.
 */
export const ReadViewContainer = ({
	children,
	isPlaceholder,
}: {
	children?: ReactNode;
	isPlaceholder?: boolean;
}): React.JSX.Element => (
	<Flex
		alignItems="center"
		xcss={[
			readViewContainerStyles,
			isPlaceholder ? readViewContainerPlaceholderStyles : readViewContainerNonPlaceholderStyles,
		]}
	>
		{children}
	</Flex>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles
export const RoleList: React.ComponentType<
	React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> & StyledProps
> = // eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
	styled2.div({
		display: 'flex',
		flexWrap: 'wrap',
		gap: token('space.100'),
	});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles
export const RoleContainer: React.ComponentType<
	React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> & StyledProps
> = // eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
	styled2.div({
		display: 'flex',
		alignItems: 'center',

		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.full'),
		paddingTop: token('space.025'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.025'),
		paddingLeft: token('space.100'),
	});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles
export const RoleDisplayName: React.ComponentType<
	React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> & StyledProps
> = // eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
	styled2.div({
		marginLeft: token('space.050'),
	});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- To migrate as part of go/ui-styling-standard
export const AvatarWrapper: React.ComponentType<
	React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> & StyledProps
> = // eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
	styled2.div({
		marginLeft: token('space.negative.050'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> div': {
			verticalAlign: 'bottom',
		},
	});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles
export const PlaceholderSpacer: React.ComponentType<
	React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> & StyledProps
> = // eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
	styled2.div({
		paddingTop: token('space.025'),
		paddingRight: 0,
		paddingBottom: token('space.025'),
		paddingLeft: 0,
	});
